import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Slider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';

const CollectionCampaignSettings = ({ collection, projects, campaigns = [], onUpdate }) => {
  const [metadata, setMetadata] = useState(collection.metadata || {});

  useEffect(() => {
    setMetadata(collection.metadata || {});
  }, [collection]);

  const handleCampaignLink = (campaignId) => {
    const updated = {
      ...metadata,
      linkedCampaignId: campaignId,
    };
    setMetadata(updated);
    onUpdate(updated);
  };

  const handleAllocationChange = (projectId, percentage) => {
    const updated = { ...metadata };
    if (!updated.projectAllocations) {
      updated.projectAllocations = [];
    }

    const existingIndex = updated.projectAllocations.findIndex(
      (a) => a.projectId === projectId
    );

    if (existingIndex >= 0) {
      if (percentage === 0) {
        updated.projectAllocations = updated.projectAllocations.filter(
          (_, i) => i !== existingIndex
        );
      } else {
        updated.projectAllocations[existingIndex].percentage = percentage;
      }
    } else if (percentage > 0) {
      updated.projectAllocations.push({ projectId, percentage });
    }

    setMetadata(updated);
    onUpdate(updated);
  };

  const getTotalAllocation = () => {
    return (metadata.projectAllocations || []).reduce(
      (sum, alloc) => sum + (alloc.percentage || 0),
      0
    );
  };

  const getAllocationForProject = (projectId) => {
    const alloc = (metadata.projectAllocations || []).find(
      (a) => a.projectId === projectId
    );
    return alloc?.percentage || 0;
  };

  const totalAllocation = getTotalAllocation();
  const allocationValid = totalAllocation === 100 || totalAllocation === 0;

  // Find the linked campaign
  const linkedCampaign = campaigns.find(c => c._id === metadata.linkedCampaignId || c.id === metadata.linkedCampaignId);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Collection Settings - {collection.name}
        </Typography>

        {/* Campaign Link */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Linked Campaign</InputLabel>
            <Select
              value={metadata.linkedCampaignId || ''}
              onChange={(e) => handleCampaignLink(e.target.value)}
              label="Linked Campaign"
            >
              <MenuItem value="">
                <em>No Campaign</em>
              </MenuItem>
              {campaigns.map((campaign) => (
                <MenuItem key={campaign._id || campaign.id} value={campaign._id || campaign.id}>
                  {campaign.name}
                  {campaign.status && (
                    <Chip
                      label={campaign.status}
                      size="small"
                      color={campaign.status === 'active' ? 'success' : 'default'}
                      sx={{ ml: 1 }}
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {linkedCampaign && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                <strong>{linkedCampaign.name}</strong>
                {linkedCampaign.startDate && linkedCampaign.endDate && (
                  <> • {linkedCampaign.startDate} to {linkedCampaign.endDate}</>
                )}
                {linkedCampaign.goalAmount && (
                  <> • Goal: ${linkedCampaign.goalAmount?.toLocaleString()}</>
                )}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Campaign details are managed in the Campaigns tab. Use the settings below to allocate funds from this collection to projects.
              </Typography>
            </Alert>
          )}

          {!metadata.linkedCampaignId && campaigns.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                This collection is not linked to any campaign. Link it to a campaign to enable fund allocation tracking.
              </Typography>
            </Alert>
          )}

          {campaigns.length === 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                No campaigns available. Create a campaign in the Campaigns tab to link this collection.
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Project Allocations */}
        <Box
          sx={{
            p: 2,
            bgcolor: allocationValid ? 'success.lighter' : 'warning.lighter',
            borderRadius: 1.5,
            border: 2,
            borderColor: allocationValid ? 'success.main' : 'warning.main',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Project Fund Allocation
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Allocate percentages of revenue from this collection to support specific projects
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              color={allocationValid ? 'success.main' : 'warning.dark'}
            >
              Total: {totalAllocation}%{' '}
              {totalAllocation === 100
                ? '✓'
                : totalAllocation === 0
                ? '(No allocations)'
                : '(must equal 100%)'}
            </Typography>
          </Box>

          {projects.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2.5 }}>
              No projects available. Create projects in the Projects tab first.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {projects.map((project) => {
                const allocation = getAllocationForProject(project.id);
                return (
                  <Box
                    key={project.id}
                    sx={{
                      p: 1.5,
                      bgcolor: allocation > 0 ? 'success.lighter' : 'background.paper',
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: allocation > 0 ? 'success.light' : 'grey.300',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {project.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          value={allocation}
                          onChange={(e) =>
                            handleAllocationChange(project.id, parseFloat(e.target.value) || 0)
                          }
                          inputProps={{ min: 0, max: 100, style: { textAlign: 'right' } }}
                          sx={{ width: 70 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          %
                        </Typography>
                      </Box>
                    </Box>
                    <Slider
                      value={allocation}
                      onChange={(e, value) => handleAllocationChange(project.id, value)}
                      min={0}
                      max={100}
                      valueLabelDisplay="auto"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}

          {!allocationValid && totalAllocation > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Allocations must total exactly 100%. Currently: {totalAllocation}%
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CollectionCampaignSettings;
